<template>
  <article v-if="event && Object.keys(event).length">
    <PostFeaturedImage
      v-if="checkFeaturedImage"
      :featured-image="getFeaturedImage"
      :srcset="getFeaturedImageSrcset"
      :is-wide="event.featuredImageWidth === 'wide'"
    ></PostFeaturedImage>
    <header>
      <nav :class="$style.back">
        <BackLink
          component="CalendarEvent"
          group="calendar"
          label="Calendar"
          name="CalendarSection"
        ></BackLink>
      </nav>
      <div :class="$style.content">
        <h1 v-html="event.title"></h1>
        <p
          v-if="event && event.eventSerieses && event.eventSerieses.nodes.length"
          class="heading-4 space-0-above"
        >
          A part of the
          <span v-for="(series, key) in event.eventSerieses.nodes" :key="key">
            <router-link
              :to="{ name: 'CalendarSeries', params: { type: 'series', slug: series.slug } }"
              :ga4-event="
                JSON.stringify({
                  click_type: 'back_link',
                  component: 'CalendarEvent',
                  content_group: 'calendar',
                  gtm_tag: 'interaction',
                  link_text: 'Back to Calendar',
                })
              "
              v-html="series.name"
            ></router-link>
            {{ getSeparator(key, event.eventSerieses.nodes.length) }}
          </span>
          series.
        </p>
      </div>
    </header>
    <div :class="$style.content">
      <section :class="$style.dates">
        <div>
          <h2>Event Dates</h2>
          <Datepicker
            v-model:selected-date="selectedDate"
            :class="$style.datepicker"
            :active-dates="activeDates"
            :is-embedded="true"
          ></Datepicker>
        </div>
        <aside v-if="Object.keys(currentOccurrence).length" :class="$style.details">
          <div v-for="(occurrence, key) in currentOccurrence" :key="key" :class="$style.detail">
            <time
              v-if="!occurrence.hideEventTimes"
              :class="$style.occurrence"
              v-html="getDateline(occurrence)"
            ></time>
            <p v-if="event.description" :class="$style.description" v-html="event.description"></p>
            <p v-if="occurrence.specificText" v-html="occurrence.specificText"></p>
            <address v-if="occurrence.venue" v-html="occurrence.venue"></address>
            <a
              v-if="occurrence.soldOutLabel"
              class="button-primary"
              disabled="disabled"
              v-html="occurrence.soldOutLabel"
            ></a>
            <a
              v-if="!occurrence.soldOutLabel && !event.multisession && occurrence.ticketLabel"
              class="button-primary"
              :href="occurrence.ticketLink"
              :ga4-event="
                JSON.stringify({
                  click_type: 'cta',
                  component: 'CalendarEvent',
                  content_group: 'calendar',
                  gtm_tag: 'link',
                  link_text: `${occurrence.ticketLabel}`,
                  link_url: `${occurrence.ticketLink}`,
                })
              "
              v-html="occurrence.ticketLabel"
            ></a>
            <a
              v-if="event.multisessionCta.link"
              class="button-primary"
              :href="event.multisessionCta.link"
              :ga4-event="
                JSON.stringify({
                  click_type: 'sold_out',
                  component: 'CalendarEvent',
                  content_group: 'calendar',
                  gtm_tag: 'interaction',
                  link_text: `${occurrence.soldOutLabel}`,
                })
              "
              v-html="event.multisessionCta.label"
            ></a>
            <AddToCalendar
              v-if="!occurrence.soldOutLabel"
              :links="occurrence.calendarLinks"
              :slug="slug"
            >
            </AddToCalendar>
          </div>
        </aside>
      </section>
    </div>
    <ProcessContent
      :class="$style.content"
      :content="event.content"
      wrapper="section"
    ></ProcessContent>
    <div :class="$style.content">
      <p v-if="event && event.eventTypes && Object.keys(event.eventTypes.nodes).length">
        Explore other
        <span v-for="(type, key) in event.eventTypes.nodes" :key="key">
          <router-link
            :to="{ name: 'CalendarSection', query: { q: type.slug } }"
            :ga4-event="
              JSON.stringify({
                click_type: 'link',
                component: `${parent}`,
                content_group: 'calendar',
                gtm_tag: 'interaction',
                link_text: `${type.name}`,
              })
            "
          >
            {{ type.name }}
          </router-link>
          {{ getSeparator(key, event.eventTypes.nodes.length) }}
        </span>
        events.
      </p>
      <aside v-if="event.multisession && activeOccurrences.length > 1">
        <p>
          This is a multi-session event, you must purchase tickets to all classes to attend the
          program.
        </p>
      </aside>
      <ContentDrawer
        v-if="event.multisession && activeOccurrences.length > 1"
        :class="$style.upcoming"
        :title="'Show Upcoming Dates'"
      >
        <ul>
          <li v-for="(item, key) in activeOccurrences" :key="key">
            <h2
              v-if="item.multisession"
              v-html="item.specificText ? item.specificText : `Session ${key + 1}`"
            ></h2>
            <time v-html="getOccurrenceDate(item, !item.hideEventTimes)"></time>
          </li>
        </ul>
      </ContentDrawer>
    </div>
  </article>
</template>

<script setup lang="ts">
import BackLink from '@/components/common/BackLink.vue';
import { calendarEventQuery, calendarOccurrencesQuery } from '@/queries/GetCalendarEvent';
import { computed, defineAsyncComponent, ref, useCssModule, watch } from 'vue';
import {
  dataLoaded,
  useDateFormat,
  useHandleError,
  useMetaData,
  useRouteParent,
} from '@/composables/Common.js';
import Datepicker from '@/components/calendar/DatePicker.vue';
import { EventInterface } from '@/types/event.interface';
import PostFeaturedImage from '@/components/common/PostFeaturedImage.vue';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { useQuery } from 'villus';
import { useRoute } from 'vue-router';

const AddToCalendar = defineAsyncComponent(() => import('@/components/calendar/AddToCalendar.vue'));
const ContentDrawer = defineAsyncComponent(
  () => import('@/components/shortcodes/ContentDrawer.vue'),
);

const props = defineProps<{
  date?: string;
  slug: string;
}>();

const route = useRoute();

const { formatDate, formatDates, formatTimeSpan } = useDateFormat();
const { handleError } = useHandleError();
const { parent } = useRouteParent();
const { setMetaDescription, setMetaTitle } = useMetaData();
const style = useCssModule();

const activeDates = ref([]);
const activeOccurrences = ref([]);
const currentOccurrence = ref([]);
const event = ref<EventInterface>({});
const lastSelectedDate = ref(null);
const occurrences = ref([]);
const occurrencesByDate = ref({});
const occurrencesReady = ref(false);
const selectedDate = ref(props.date);

const checkFeaturedImage = computed(() => {
  return (
    (occurrencesReady.value &&
      event.value &&
      event.value.featuredImage &&
      event.value.featuredImage.node) ||
    (currentOccurrence.value &&
      currentOccurrence.value[0] &&
      currentOccurrence.value[0].specificImage)
  );
});
const getFeaturedImage = computed(() => {
  if (
    currentOccurrence.value &&
    currentOccurrence.value[0] &&
    currentOccurrence.value[0].specificImage
  ) {
    return currentOccurrence.value[0].specificImage;
  }

  if (
    occurrencesReady.value &&
    event.value &&
    event.value.featuredImage &&
    event.value.featuredImage.node
  ) {
    return event.value.featuredImage.node;
  }

  return null;
});
const getFeaturedImageSrcset = computed(() => {
  if (
    currentOccurrence.value &&
    currentOccurrence.value[0] &&
    currentOccurrence.value[0].specificImage &&
    currentOccurrence.value[0].specificImage.imageSrcset
  ) {
    return currentOccurrence.value[0].specificImage.imageSrcset;
  }

  if (occurrencesReady.value && event.value && event.value.imageSrcset) {
    return event.value.imageSrcset;
  }

  return '';
});

const { data } = useQuery({
  onData: (data) => {
    if (data.event && Object.keys(data.event).length) {
      event.value = data.event;
      setMetaTitle(data.event.title);
      setMetaDescription(data.event.excerpt);
      dataLoaded();
      execute();
    }

    if (!data.event || Object.keys(data.event).length === 0) {
      handleError('CalendarEvent', props.slug, true, '/calendar');
    }
  },
  query: calendarEventQuery,
  variables: { slug: props.slug },
});

const queryVars = computed(() => {
  if (event.value.eventId) {
    return { eventId: event.value.eventId.toString() };
  }

  return '';
});

const {
  data: queryData,
  error,
  execute,
} = useQuery({
  fetchOnMount: false,
  onData: (data) => {
    if (data) {
      activeOccurrences.value = [];
      occurrences.value = data.eventOccurrences.nodes;
      Object.keys(occurrences.value).map((key) => {
        const tempObj = occurrences.value[key];

        if ('today' === tempObj.start.status || 'future' === tempObj.start.status) {
          activeOccurrences.value.push(tempObj);

          // Date keyed object for datepicker selections.
          occurrencesByDate.value[tempObj.start.isoDate] = tempObj;

          // Enable only active dates in datepicker.
          activeDates.value.push(tempObj.start.isoDate);
        }
      });

      const lastDateKey = activeDates.value[activeDates.value.length - 1];

      // Date param is a valid occurrence date.
      if (props.date && activeDates.value.indexOf(props.date) > -1) {
        selectedDate.value = props.date;
      }

      // Date param is after last valid occurrence date.
      if (props.date && props.date > lastDateKey) {
        selectedDate.value = lastDateKey;
        history.pushState({}, null, `/event/${route.params.slug}/${lastDateKey}`);
      }

      // Date param is before first valid occurrence date.
      if (props.date && props.date < lastDateKey && activeDates.value.indexOf(props.date) === -1) {
        let skip = false;

        activeDates.value.forEach((date) => {
          if (skip) {
            return;
          }

          if (props.date < date) {
            selectedDate.value = date;
            history.pushState({}, null, `/event/${route.params.slug}/${date}`);
            skip = true;
          }
        });
      }

      // No date param, use first valid occurrence date.
      if (!props.date) {
        selectedDate.value = activeDates.value[0];
      }

      currentOccurrence.value = activeOccurrences.value.reduce((result, item) => {
        if (selectedDate.value === item.start.isoDate) {
          result.push(item);
        }

        return result;
      }, []);

      occurrencesReady.value = true;
    }

    if (!data) {
      handleError('CalendarEvent', error);
    }
  },
  query: calendarOccurrencesQuery,
  variables: queryVars,
});

watch(selectedDate, (val) => {
  currentOccurrence.value = activeOccurrences.value.reduce((result, item) => {
    if (val === item.start.isoDate) {
      result.push(item);
    }

    return result;
  }, []);

  if (lastSelectedDate.value !== val) {
    if (lastSelectedDate.value) {
      history.pushState({}, null, `/event/${route.params.slug}/${val}`);
    }

    lastSelectedDate.value = val;
  }
});

/**
 * Generates the dateline HTML.
 *
 * @param {*} occurrence
 */
function getDateline(occurrence: EventInterface): string {
  let str = `<span class="${style.date}">`;
  str += 'today' === occurrence.start.status ? 'Today' : occurrence.start.weekday;
  str += ',&nbsp;';
  str += `${formatDates([{ start: occurrence.start }, { start: occurrence.end }])}</span>`;

  if (!occurrence.hideStartTime) {
    str += `<span class="${style.time} subtitle-2">${formatTimeSpan(occurrence)}</span>`;
  }

  return str;
}

function getOccurrenceDate(occurrence: EventInterface, showTime = true): string {
  return showTime
    ? `${formatDate(occurrence.start)}, ${formatTimeSpan(occurrence)}`
    : `${formatDate(occurrence.start)}`;
}

function getSeparator(key: number, length: number): string {
  if (length > 1 && length <= 2 && key + 1 < length) {
    return ' and ';
  }

  if (length > 2 && key + 1 === length - 1) {
    return ' and ';
  }

  if (length > 2 && key + 1 < length - 1) {
    return ', ';
  }
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: calendarEventQuery,
          variables: {
            slug: params.slug,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss">
@use '@/styles/links.module.scss' as *;
</style>

<style lang="scss" module>
.datepicker {
  margin-top: 1rem;
}

.dates {
  @include space-22-27-above;

  h2 {
    @include space-0-above;
  }
}

.detail + .detail {
  margin-top: 2rem;
}

.occurrence {
  & + * {
    @include space-12-16-above;
  }

  .date {
    @include heading-4;
  }

  .date,
  .time {
    display: block;
  }
}

.occurrences {
  margin-top: 2rem;
}

.upcoming {
  margin-top: 2rem;

  ul {
    margin: 0;
    padding-left: 0;
  }

  li {
    list-style: none;
    margin-top: 2rem;

    &:first-of-type {
      margin-top: 0;
    }
  }

  time {
    @include heading-4;

    & {
      display: block;
      margin-top: 1rem;
    }
  }
}

@media (min-width: $break-point) {
  .content {
    @include grid-content;

    .dates {
      display: grid;
      grid-column: 1 / span 6;
      grid-column-gap: $column-gutter-large;
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .occurrence {
    .date {
      @include space-0-above;
    }
  }
}
</style>
